import type { ChartOption } from '@app-types/chart';
import type { QuarterlyPeriodChartData, QuarterlyPeriodPoint } from '@app-types/performanceTrends';
import { formatNumber } from '@utils/format';
import { CHART_AXIS_LABEL_STYLE, CHART_COLORS, CHART_SCROLLBAR_COLORS } from './chartConstants';
import { formatMillionsAxisLabel } from './performanceTrendOptions';
import { QUARTERLY_GRID_RIGHT, type QuarterlyViewport } from './quarterlyViewport';

const escapeHtml = (value: string): string => value
  .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
  .replace(/"/g, '&quot;').replace(/'/g, '&#39;');

export const formatQuarterlyPeriodLabel = (point: QuarterlyPeriodPoint): string => {
  const match = point.periodEndDate.match(/^(\d{4})[/-](\d{1,2})/);
  const period = match ? `${match[1].slice(-2)}/${match[2].padStart(2, '0')}` : '--';
  return `${period}${point.isForecast ? '予' : ''}`;
};

/** Reserve the same Y-label gutter for layout and viewport capacity. */
export const quarterlyGridLeft = (data: QuarterlyPeriodChartData): number => {
  const labels = [data.yAxisMin, data.yAxisMax,
    ...Array.from({ length: 5 }, (_, index) => data.yAxisMin + index * data.yAxisInterval)]
    .map(formatMillionsAxisLabel);
  return Math.max(48, Math.max(...labels.map((label) => label.length)) * 7 + 12);
};

export const formatQuarterlyPeriodTooltip = (params: unknown, unit: string): string => {
  const item = Array.isArray(params) ? params[0] : params;
  if (!item || typeof item !== 'object' || !('data' in item)) return '';
  const datum = item.data as { point?: QuarterlyPeriodPoint } | null;
  const point = datum?.point;
  if (!point || typeof point.label !== 'string') return '';
  const title = `${point.label} ${point.quarter}${point.isForecast ? '予' : ''}`;
  const value = typeof point.value === 'number' && Number.isFinite(point.value)
    ? formatNumber(point.value, 2) : '--';
  return `${escapeHtml(title)}<br/>${value}${unit ? ` ${escapeHtml(unit)}` : ''}`;
};

export const buildQuarterlyPeriodOption = (
  data: QuarterlyPeriodChartData,
  viewport: QuarterlyViewport,
): ChartOption => {
  const left = quarterlyGridLeft(data);
  const pointsByKey = new Map(data.points.map((point) => [point.key, point]));
  const scrollable = viewport.end - viewport.start + 1 < data.points.length;
  return {
    grid: { left, right: QUARTERLY_GRID_RIGHT, top: 8, bottom: 84 },
    tooltip: {
      trigger: 'axis',
      confine: true,
      formatter: (params: unknown) => formatQuarterlyPeriodTooltip(params, data.unit),
    },
    xAxis: {
      type: 'category',
      data: data.points.map((point) => point.key),
      axisTick: { show: false },
      axisLine: { lineStyle: { color: CHART_COLORS.grid } },
      axisLabel: {
        ...CHART_AXIS_LABEL_STYLE,
        rotate: 90,
        interval: 0,
        margin: 8,
        // ECharts' label index is relative to the zoom window; the category
        // value remains the stable period key even after horizontal scrolling.
        formatter: (value: string) => {
          const point = pointsByKey.get(value);
          return point ? formatQuarterlyPeriodLabel(point) : '';
        },
      },
    },
    yAxis: {
      type: 'value',
      min: data.yAxisMin,
      max: data.yAxisMax,
      interval: data.yAxisInterval,
      axisLabel: { ...CHART_AXIS_LABEL_STYLE, formatter: formatMillionsAxisLabel },
      splitLine: { lineStyle: { color: CHART_COLORS.grid } },
      axisLine: { show: false },
      axisTick: { show: false },
    },
    dataZoom: data.points.length ? [{
      type: 'slider',
      xAxisIndex: 0,
      show: scrollable,
      filterMode: 'none',
      zoomLock: true,
      brushSelect: false,
      showDataShadow: false,
      showDetail: false,
      handleSize: 0,
      moveHandleSize: 0,
      left,
      right: QUARTERLY_GRID_RIGHT,
      bottom: 4,
      height: 8,
      borderRadius: 4,
      borderColor: CHART_SCROLLBAR_COLORS.track,
      backgroundColor: CHART_SCROLLBAR_COLORS.track,
      fillerColor: CHART_SCROLLBAR_COLORS.thumb,
      startValue: viewport.start,
      endValue: viewport.end,
      rangeMode: ['value', 'value'],
    }] : [],
    series: [{
      type: 'bar',
      name: data.title,
      barWidth: 8,
      clip: true,
      itemStyle: { color: CHART_COLORS.seriesPrimary },
      data: data.points.map((point) => ({ value: point.value, point })),
    }],
  };
};
